/**
 * Visitor uploads: presign, PUT straight to storage, hand back a key.
 *
 * XHR rather than fetch so the merchant sees progress. A support widget is
 * often the place someone uploads a 10 MB screen recording over hotel wifi,
 * and a spinner with no number reads as broken.
 *
 * Tracing follows the widget's existing `data-debug` flag — the widget runs on
 * a merchant's own page, so it must be silent unless someone has asked it not
 * to be. A presigned URL is never logged: it is a bearer token for the object
 * it names, and this console belongs to someone else.
 */

const log = (debug, message, data) => {
  if (!debug) return;
  if (data === undefined) console.info(`[superchat:upload] ${message}`);
  else console.info(`[superchat:upload] ${message}`, data);
};

/** Origin of a presigned URL — safe to log, unlike the signature on the end. */
function safeHost(url) {
  try {
    return new URL(url).origin;
  } catch {
    return 'unparseable';
  }
}

export function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** i;
  return `${value >= 10 || i === 0 ? Math.round(value) : value.toFixed(1)} ${units[i]}`;
}

export const isImage = (mimeType) => String(mimeType ?? '').startsWith('image/');

function put(url, file, headers, onProgress, debug) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    for (const [name, value] of Object.entries(headers ?? {})) xhr.setRequestHeader(name, value);

    const startedAt = Date.now();
    log(debug, 'PUT to storage started', { name: file.name, size: file.size, host: safeHost(url) });

    // Deciles only: on a fast link progress fires many times a second, and a
    // log line per event buries whatever else the merchant's page is saying.
    let lastDecile = -1;
    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) return;
      const fraction = event.loaded / event.total;
      const decile = Math.floor(fraction * 10);
      if (decile > lastDecile) {
        lastDecile = decile;
        log(debug, `${decile * 10}%`, { name: file.name });
      }
      onProgress?.(fraction);
    };

    xhr.onload = () => {
      const ms = Date.now() - startedAt;
      if (xhr.status >= 200 && xhr.status < 300) {
        log(debug, 'PUT to storage succeeded', { name: file.name, status: xhr.status, ms });
        resolve();
        return;
      }
      // A 403 is usually a signature that no longer matches what was sent —
      // an expired URL, or a header the browser changed on the way out.
      log(debug, 'PUT to storage failed', {
        name: file.name,
        status: xhr.status,
        ms,
        response: String(xhr.responseText ?? '').slice(0, 400),
      });
      reject(new Error(`Upload failed (${xhr.status})`));
    };

    xhr.onerror = () => {
      // Status 0 with no response almost always means the bucket's CORS rules
      // refused the request before it left the browser.
      log(debug, 'PUT to storage could not be sent', {
        name: file.name,
        hint: 'usually the bucket CORS policy — it must allow PUT from this origin',
      });
      reject(new Error('The upload could not reach storage'));
    };

    xhr.send(file);
  });
}

export async function uploadFile(api, file, { onProgress, debug = false } = {}) {
  const mimeType = file.type || 'application/octet-stream';
  log(debug, 'picked a file', { name: file.name, size: file.size, type: mimeType });

  let target;
  try {
    target = await api.signUpload({ fileName: file.name, mimeType, size: file.size });
  } catch (err) {
    // A 503 is a server with no bucket configured, not a problem with the file.
    log(debug, 'signing failed', { name: file.name, status: err.status, code: err.code });
    throw err;
  }

  log(debug, 'signed', { key: target.key, expiresIn: target.expiresIn });
  await put(target.uploadUrl, file, target.headers, onProgress, debug);

  return {
    key: target.key,
    name: file.name,
    mimeType,
    size: file.size,
    url: target.publicUrl ?? null,
  };
}

/** Public bucket: already have it. Private: one round trip, expires in minutes. */
export async function attachmentUrl(api, attachment, { download = false, debug = false } = {}) {
  if (attachment.url && !download) return attachment.url;

  try {
    const signed = await api.uploadLink(attachment.key, { download });
    log(debug, 'download link signed', { key: attachment.key, expiresIn: signed.expiresIn });
    return signed.url;
  } catch (err) {
    log(debug, 'download link refused', {
      key: attachment.key,
      status: err.status,
      code: err.code,
    });
    throw err;
  }
}

export default { uploadFile, attachmentUrl, formatBytes, isImage };
